/**
 * Leaving a household, removing a partner, and discovering you were removed.
 *
 * All three end in the same place — personal scope, repopulated — so all three
 * go through returnToSoloScope here. The order is the same one the join screen
 * uses in reverse, and it is not arbitrary: every synced slice reads
 * `state.auth.householdId` inside its own thunk to decide which Firestore path
 * to hit, so scope must flip *before* the re-pull or the re-pull reads the
 * household we just left.
 *
 * What is new here is the check at the end. The server reports how many
 * documents it copied back; the client reports how many it can actually see.
 * A leave that says "226 recipes are yours" and then shows an empty library is
 * the exact failure this endpoint exists to prevent, and it is much cheaper to
 * catch here than in a support message a week later.
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import { setHouseholdId } from '../redux/authSlice';
import { downloadMealPlanFromCloud } from '../redux/mealPlanSlice';
import { downloadGroceryListFromCloud } from '../redux/groceryListSlice';
import { downloadInventoryFromCloud } from '../redux/inventorySlice';
import { downloadRecipeMetadataFromCloud } from '../redux/recipeMetadataSlice';
import { downloadUserPreferencesFromCloud } from '../redux/userPreferencesSlice';
import {
  leaveHousehold,
  removePartner,
  HouseholdError,
  type LeftHousehold,
} from '../services/householdService';

export interface ExitOutcome extends LeftHousehold {
  /**
   * False when the server said it restored data and the re-pull could not find
   * it. The exit itself has already happened — this reports that the app in
   * front of the user is not showing what they were promised, which is a
   * reason to tell them to reload rather than a reason to celebrate.
   */
  verified: boolean;
  /** What the re-pull actually saw, for the failure message and the logs. */
  observedRecipeMetadata: number;
}

export const useHouseholdExit = () => {
  const dispatch = useDispatch<AppDispatch>();
  const uid = useSelector((s: RootState) => s.auth?.user?.uid ?? null);

  /**
   * Flip to personal scope and repopulate from `users/{uid}/…`.
   *
   * Returns how many recipeMetadata documents the re-pull actually read.
   * That slice is the probe because its thunk reports what it fetched from the
   * new path; the inventory thunk merges the cloud result into what is already
   * in memory, so its count stays high even when the read returns nothing and
   * it cannot tell us anything.
   */
  const returnToSoloScope = useCallback(async (): Promise<number> => {
    if (!uid) return 0;

    // Also clears the module-level householdContext, which is what the
    // services that cannot reach Redux (unifiedRecipeService, recipeService,
    // AddRecipeModal) read when they build a path. Leaving it set would keep
    // saving recipes into a household we are no longer in.
    dispatch(setHouseholdId(null));

    const [, , , metadata] = await Promise.all([
      dispatch(downloadMealPlanFromCloud(uid)).unwrap().catch(() => null),
      dispatch(downloadGroceryListFromCloud(uid)).unwrap().catch(() => null),
      dispatch(downloadInventoryFromCloud(uid)).unwrap().catch(() => null),
      dispatch(downloadRecipeMetadataFromCloud(uid))
        .unwrap()
        .catch(() => null),
      dispatch(downloadUserPreferencesFromCloud(uid)).unwrap().catch(() => null),
    ]);

    return (metadata as { count?: number } | null)?.count ?? 0;
  }, [dispatch, uid]);

  const finish = useCallback(
    async (result: LeftHousehold): Promise<ExitOutcome> => {
      const observedRecipeMetadata = await returnToSoloScope();
      const promised = result.restored.recipeMetadata ?? 0;
      return {
        ...result,
        observedRecipeMetadata,
        verified: promised === 0 || observedRecipeMetadata > 0,
      };
    },
    [returnToSoloScope]
  );

  /** Leave the household I am in. */
  const leave = useCallback(async (): Promise<ExitOutcome> => {
    if (!uid) {
      throw new HouseholdError('not-authorized', 'Not signed in');
    }
    return finish(await leaveHousehold(uid));
  }, [uid, finish]);

  /**
   * Remove the other member. My own scope does not change — the household and
   * everything in it stays mine — so this deliberately does NOT re-pull.
   */
  const remove = useCallback(
    async (partnerUid: string): Promise<LeftHousehold> => {
      if (!uid) {
        throw new HouseholdError('not-authorized', 'Not signed in');
      }
      return removePartner(uid, partnerUid);
    },
    [uid]
  );

  return { leave, remove, returnToSoloScope };
};
